import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxesPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InfoBubble, InfoBubbleInstanceProps} from '../../../libs/shared/ui/src/components/tip/infoBubble';
import {InfoBubbleContent} from '../../../libs/shared/ui/src/components/tip/infoBubbleContent';
import {InfoBubbleDescription} from '../../../libs/shared/ui/src/components/tip/infoBubbleContentDescription';
import {InfoBubbleTitle} from '../../../libs/shared/ui/src/components/tip/infoBubbleContentTitle';
import {InfoBubbleFooterActions} from '../../../libs/shared/ui/src/components/tip/infoBubbleFooterActions';
import {
  InfoBubblePill,
  InfoBubblePillVariantsEnum,
} from '../../../libs/shared/ui/src/components/tip/infoBubblePill';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import headerImage from '../../assets/images/viewWithTags.svg';
import {useAppDispatch} from '../../helpers/appReduxHelpers';
import {OnboardingTipTypesEnum} from '../../models/api/preferencesModel';
import {showCreateLensDialog} from '../../store/panels/inboxes/inboxesPanelActions';
import {Image} from '../common/image';
import {useOnboardingInfoBubblePresenter} from './onboardingInfoBubbleHook';

/*
 * Props.
 */

interface LensesTagSearchInfoBubbleProps extends InfoBubbleInstanceProps {
  panelId: InboxesPanelId;
}

/*
 * Styles.
 */

const StyledBubbleBodyDiv = styled.div`
  padding: 0px 28px 21px 28px;
`;

/*
 * Component.
 */

export const LensesTagSearchInfoBubble: FC<LensesTagSearchInfoBubbleProps> = ({anchor, panelId}) => {
  const {isVisible, closeInfoBubble: onClose} = useOnboardingInfoBubblePresenter(
    OnboardingTipTypesEnum.LENSES_TAG_SEARCH,
  );
  const dispatch = useAppDispatch();
  const onShowMe = () => {
    onClose();
    dispatch(showCreateLensDialog(panelId));
  };

  return (
    <InfoBubble
      position="right-end"
      anchor={anchor}
      isVisible={isVisible}
      isInteractive
      interactionId="lenses-tag-search-bubble"
      overlayOffset={0}
      overlayBorderRadius={0}
      onRequestClose={onClose}
    >
      <InfoBubbleContent $removePadding>
        <Image src={headerImage} width={380} height={142} />
        <StyledBubbleBodyDiv>
          <InfoBubblePill type={InfoBubblePillVariantsEnum.EDUCATION} />
          <InfoBubbleTitle shouldWrapTitle>
            <Trans id="infobubble-lenses-tag-search-title">
              Constantly searching with the same shared tag?
            </Trans>
          </InfoBubbleTitle>
          <InfoBubbleDescription>
            <Trans id="infobubble-lenses-tag-search-description">
              If you regularly work on conversations with this tag you can instead create a view with this tag
              across all relevant inboxes.
            </Trans>
          </InfoBubbleDescription>
          <InfoBubbleFooterActions $justifyContentRight>
            <Button
              onClick={() => {
                onClose();
              }}
              size={VisualSizesEnum.MEDIUM}
              variant={ButtonVariantsEnum.SECONDARY}
            >
              <Trans id="infobubble-lenses-tag-search-not-now">Not now</Trans>
            </Button>
            <Button onClick={onShowMe} size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.PRIMARY}>
              <Trans id="infobubble-lenses-tag-search-show-me">Show me</Trans>
            </Button>
          </InfoBubbleFooterActions>
        </StyledBubbleBodyDiv>
      </InfoBubbleContent>
    </InfoBubble>
  );
};
